/**
 * Front-door copy (#3123, child of the #3122 marketing epic).
 *
 * WHY THIS IS A DATA MODULE AND NOT INLINE JSX
 * --------------------------------------------
 * `scripts/check-voice.mjs` scans `frontend/src/data/content/*.ts`. Inline JSX
 * strings are inside `oro-voice`'s stated scope but OUTSIDE the gate's roots
 * (#2954), so copy written straight into `HomePage.tsx` gets no mechanical
 * check for em-dashes or British spellings. Putting it here means the gate is
 * real for this page from the first commit rather than after #2954 lands.
 *
 * NOT a `ContentDoc`. The legal and About/FAQ pages are documents rendered by
 * `ContentDocView`; this is a marketing layout with fixed slots, so it is a
 * flat typed record. `brandCadence.test.ts` walks ContentDoc shapes only and
 * does not see this file — the ™ pin for this page lives in
 * `HomePage.landing.test.tsx`, which counts rendered glyphs instead.
 *
 * THE ™ IS NOT IN THIS FILE, DELIBERATELY.
 * Core Rule 24 gives the page exactly one mark, on its most prominent use. That
 * is the hero subhead, and it renders through `<OroTrademark />` so the mark
 * stays a component rather than a hardcoded glyph. The string here is only the
 * remainder of that sentence — see `heroSubheadAfterBrand`.
 *
 * DEMO DATA ONLY. Section 3 shows the product using the seeded Golden Hour
 * club. Never a real club, never a member's name or face (#3122 imagery rule).
 */

export const LANDING = {
  /** Section 2 — hero. */
  hero: {
    /**
     * The old headline, kept as the eyebrow (#3511). The h1 now says what the
     * product is for; the three verbs stay as the brand line above it.
     */
    eyebrow: 'Plan. Share. Move.',
    headline: 'The current plan for your club, in one place.',
    /**
     * Rendered as `<OroTrademark /> {heroSubheadAfterBrand}`, so the sentence
     * reads "ORO move™ is a free app for…". Naming the product here is what
     * closes the B3 finding: the swap test showed the headline could not carry
     * "what is this" alone once the full-bleed photograph was removed.
     *
     * #3511 asked for "mobile-first". The concrete form is used instead: there
     * is an iPhone app and a web app, and no native Android app, so "on iPhone
     * and the web" is the claim a reader can check. "free" is the app, not
     * every feature; the offer line below says where the line is (owner
     * decision on #3512, 2026-10-05). "any club that gets together to move" is
     * the #1186 archetype line folded into #3516.
     */
    heroSubheadAfterBrand:
      'is a free app for running clubs, cycling clubs, and any club that gets together to move. Events, RSVPs, updates, check-ins, and conversation, on iPhone and the web.',
    /**
     * The leader path is the primary action (#3517): the leader is the buyer.
     * Signed out, it opens sign-in with a redirect to event creation; a brand
     * new account goes through onboarding (which offers to create the club)
     * first.
     */
    primaryCta: 'Set up your next event',
    secondaryCta: 'See how it works',
    /**
     * Quiet text action for returning members (owner decision 2026-10-05).
     * At phone width the header's Sign in sits behind the menu.
     */
    signIn: 'Sign in',
    /**
     * The free offer (#3512). Owner decision 2026-10-05: "free forever"
     * attaches to the club's CORE, never to everything; leader tiers (Club
     * Supporter, ADR-0017) are separate. Backed by
     * `docs/canonical/business/subscription-tiers.md` ("run their entire club —
     * forever — on Free"). The second sentence is future tense on purpose
     * (owner decision 2026-10-05): Club Supporter billing is not wired, so the
     * page must not say leader tools are available now (Rule 33).
     */
    offer: 'The core is free for every club, forever. Extra tools for leaders are on the way.',
  },

  /** Section 3 — the week, shown. The fix for "the app is never shown". */
  shown: {
    eyebrow: 'Built to get out of the way',
    title: 'The tools the club needs. Without the noise.',
    lede: 'No feed to maintain. No workout to upload. Just the event, the people, and the conversation.',

    eventCap: 'Event',
    // Coopers Rock (the demo event's location). Unsplash+ — Rocky Friz,
    // photo 0ZLjlmYB4Gk. Place/landscape, no faces (#3122 imagery rule).
    eventHeroImage: '/images/rocky-friz-0ZLjlmYB4Gk-unsplash.jpg',
    // Describes the PHOTO only (#3518). The card's title, time, place and
    // count are visible text right below it, so repeating them here would make
    // a screen reader say them twice. No ™ in alt text (Rule 24, #2450).
    eventHeroAlt: 'A leaf-covered trail through a misty forest',
    eventDay: '14',
    eventMonth: 'Mar',
    eventTitle: 'Saturday Long Run',
    eventTime: '6:40 AM',
    eventPlace: 'Coopers Rock lot',
    eventBlurb: 'Eight to twelve miles, easy pace up front. We wait at the top.',
    eventGoing: '12 going',
    eventOverflow: '+9',

    clubCap: 'Club',
    // Golden-hour group walking together, silhouetted (no legible faces) —
    // on-anchor "moving together, nobody the hero". Unsplash+ — Hester Qiang,
    // photo LhY8gh5_u_g (#3122 imagery rule).
    clubHeroImage: '/images/hester-qiang-LhY8gh5_u_g-unsplash.jpg',
    // Photo only, same reason as `eventHeroAlt` (#3518).
    clubHeroAlt: 'A small group walking together at sunset, in silhouette',
    clubName: 'Golden Hour',
    clubMeta: 'Morgantown, WV · 34 members',
    clubLeaderTag: 'Leader',

    /**
     * The roster. Same people as the thread below, and they must stay the same
     * people: they were independently hardcoded in the page AND the proof
     * template, so an edit to one left the other showing a different club.
     * Caught on PR #3173 round 2.
     */
    members: [
      { name: 'Jamie R.', leader: true },
      { name: 'Marcus K.', leader: false },
      { name: 'Ana T.', leader: false },
    ],

    /**
     * The cycling example (#3516): the homepage never said "cycling". Same
     * demo club (Golden Hour), the Tuesday after the long run. No photo: it
     * is a compact card, and no ride image has been acquired.
     */
    ride: {
      day: '17',
      month: 'Mar',
      title: 'Tuesday Night Ride',
      time: '6:00 PM',
      place: 'Caperton Trail',
      blurb: 'Twenty-five easy miles, out and back. No-drop, lights on.',
      going: '8 going',
    },

    checkinCap: 'Check-in',
    checkinCount: '11',
    checkinLabel: 'checked in this morning',
    checkinBlurb:
      'Members tap once at the trailhead. Nobody takes attendance on a clipboard.',

    // "Discuss" is the app's own name for chat (the Discuss nav tab / LOBBY
    // room), so the demo cap matches what a member actually sees.
    threadCap: 'Discuss',

    /**
     * The thread itself. These ARE user-facing copy, so they live here rather
     * than inline in the page: inline JSX is outside check-voice.mjs's roots
     * (#2954) and outside gallery's round trip, which would leave the only
     * conversational prose on the page ungated and un-editable. Caught in
     * review of PR #3173.
     *
     * `from` is an INDEX INTO `members` above, not a name. Rounds 2 and 3 of
     * that review kept finding the same defect one field over: the roster and
     * the thread were two independent lists of the same three people, so
     * renaming a member through the proof left them still talking under the old
     * name. An index cannot disagree with the roster.
     *
     * It is a number rather than a string id on purpose. Galley makes every
     * STRING in this file editable and has no ignore list, so a string id would
     * appear in the proof as if it were copy and a well-meaning editor could
     * rename it and break the page. Numbers are invisible to the adapter, and
     * `as const` still resolves each one to a specific member at typecheck.
     *
     * `self: true` is the viewer's own bubble — a fact about whose screen this
     * is, not about the person, so it stays here. The leader tag is read from
     * the member record instead, which is where leadership actually lives.
     * Demo data (Golden Hour), never a real member.
     */
    thread: [
      { from: 1, text: 'Anyone parking at the lower lot? Gate was locked last week.', self: false, reactions: [] },
      { from: 2, text: "Upper lot is open. I'll be there 6:30 if anyone wants to warm up.", self: false, reactions: [{ emoji: '👍', count: 3 }] },
      { from: 0, text: 'Upper lot it is. I moved the pin on the event.', self: true, reactions: [{ emoji: '🙌', count: 2 }] },
    ],
  },

  /** Section 4 — three things it does. Type only, no icons. */
  things: {
    eyebrow: 'How it works',
    title: 'Make a plan. Notify the crew. Move on.',
    items: [
      {
        num: '01',
        heading: 'Post what\'s next.',
        body: 'A run, ride, walk, workout: whatever your club is doing. Put the when, where, and what-to-expect in one clear place.',
      },
      {
        num: '02',
        heading: 'Get the update.',
        body: 'Something change? Get a notification or update to your calendar automatically so you never miss out.',
      },
      {
        num: '03',
        heading: 'Move together.',
        body: 'Members can check in when they arrive. No clipboards or spreadsheets.',
      },
    ],
  },

  /** Section 5 — which one are you. */
  doors: {
    eyebrow: 'Two ways in',
    title: 'Which one are you?',
    leader: {
      heading: 'I run the club',
      // The switchboard line (#3516): the leader's pain in their own words.
      // The second sentence is the line the leaders page ships (leaders.ts
      // `reality`); HomePage.landing.test.tsx pins the match.
      body: 'You make the plans. And when someone needs to know what\'s going on, they ask you.',
      cta: 'Getting started for leaders',
    },
    member: {
      heading: 'I show up',
      body: 'You want to know what\'s happening, where to be, and if something changed.',
      cta: 'What members get',
    },
  },

  /**
   * Section 6 — the door. Carries the page's one gold action.
   *
   * (corrected 2026-10-05: this said "No cost language: #3122 D1 took 'Free
   * to start' off every public page until ADR-0017 settles the plan." The
   * owner superseded #3122 D1 on 2026-10-05: cost language is allowed. The
   * core is free forever, and leaders can opt into Club Supporter, price not
   * set. The offer line now sits under the hero, #3512.)
   *
   * The action is the leader path (#3517), repeated from the hero, with the
   * how-to guide one link away. Members sign in from the hero's quiet
   * "Sign in" or the header.
   */
  close: {
    title: 'Where your club\'s plan lives.',
    body: 'Manage less. Move more.',
    cta: 'Set up your next event',
    guideCta: 'Read the leader guide',
  },
} as const;
